/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { EChartsOption } from "echarts";
import { BehaviorSubject, Subject, tap } from "rxjs";
import { ButtonComponent } from "../../shared/components/button/button.component";
import { ChartComponent } from "../../shared/components/chart/chart.component";
import { DatepickerComponent } from "../../shared/components/datepicker/datepicker.component";
import { DropdownComponent } from "../../shared/components/dropdown/dropdown.component";
import { MapComponent } from "../../shared/components/map/map.component";
import { MapChart } from "../../shared/models/http/charts/map-chart/map-chart.model";
import { ChartPattern, ChartResponse } from "../../shared/models/ui/charts.model";
import { SelectOption } from "../../shared/models/ui/select-option.model";
import { TimePeriodRange } from "../../shared/models/ui/time-period-range.model";
import { AuthorizationService } from "../../shared/services/authorization.service";
import { RegionsService } from "../../shared/services/regions.service";
import { StorageService } from "../../shared/services/storage.service";
import { WhatIfCarbonIntensityService } from "../../shared/services/what-if-carbon-intensity.service";
import { COLORS, REGIONS } from "../../shared/utils/constants";
import {
	AVERAGE_CARBON_INTENSITY_BAR_CHART_OVERRIDES,
	CHART_PATTERNS
} from "../../shared/utils/echarts/chart-constants";
import { getRangesColorForMapItemAreaColor } from "../../shared/utils/echarts/chart-utils";
import { GRANTS } from "../../shared/utils/grants.constants";
import { lazyLoad } from "../../shared/utils/lazy-load";
import { getCurrentWeekRange } from "../../shared/utils/reporting-period";
import { formatDateForBE } from "../../shared/utils/utils";

@Component({
	selector: "globalictcc-regional-emissions",
	standalone: true,
	imports: [
		CommonModule,
		DatepickerComponent,
		ButtonComponent,
		ChartComponent,
		DropdownComponent,
		ReactiveFormsModule,
		MapComponent
	],
	templateUrl: "./regional-emissions.component.html",
	styleUrl: "./regional-emissions.component.scss"
})
export class RegionalEmissionsComponent implements OnInit, OnDestroy {
	public readonly grants = GRANTS.regionalEmissions;
	public readonly authorizationService: AuthorizationService;

	public countryOptions: SelectOption[] = REGIONS;

	public timePeriodForm = new FormGroup({
		timePeriod: new FormControl<TimePeriodRange>(getCurrentWeekRange(), [Validators.required])
	});

	public regionForm = new FormGroup({
		region: new FormControl<string>("ITA", [Validators.required])
	});

	private readonly _averageCarbonIntensityFilters$ = new BehaviorSubject<
		{ startDate: string; endDate: string; tenantId: string } | undefined
	>(undefined);
	private readonly _carbonIntensityFilters$ = new BehaviorSubject<
		{ region: string; startDate: string; endDate: string } | undefined
	>(undefined);
	public readonly averageCarbonIntensityByRegion$ = lazyLoad(
		this._averageCarbonIntensityFilters$,
		filters =>
			this._regionsService.getAverageCarbonIntensity(
				filters.startDate,
				filters.endDate,
				filters.tenantId
			)
	);
	public readonly carbonIntensityByCountry$ = lazyLoad(this._carbonIntensityFilters$, filters =>
		this._regionsService.getCarbonIntensityByRegion(
			filters.region,
			filters.startDate,
			filters.endDate
		)
	);

	public regionsMapChart?: ChartResponse<MapChart>;
	public isRegionsMapChartLoading = false;
	public regionsMapChartOverrides: EChartsOption;
	public barColors: string[] = [COLORS.GREEN_50, COLORS.YELLOW_100];
	public barOverrides: EChartsOption = AVERAGE_CARBON_INTENSITY_BAR_CHART_OVERRIDES;
	public barPatterns: ChartPattern[] = [null, CHART_PATTERNS.SQUARES];
	public lineOverrides: EChartsOption = {
		series: {
			color: COLORS.GREEN_50,
			smooth: true,
			showSymbol: false
		}
	};

	private _tenantId: string;
	private _destroy$ = new Subject<void>();

	constructor(
		private _regionsService: RegionsService,
		private _storageService: StorageService,
		private _whatIfCarbonIntensityService: WhatIfCarbonIntensityService,
		authorizationService: AuthorizationService
	) {
		this.authorizationService = authorizationService;
	}

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();

		this.fetchCharts();
	}

	ngOnDestroy() {
		this._destroy$.next();
		this._destroy$.complete();
	}

	/** Applies the selected filters to both chart streams and reloads the ungated regions map. */
	public fetchCharts() {
		if (
			!this._tenantId ||
			!this.timePeriodForm?.controls?.timePeriod.value ||
			!this.regionForm?.controls?.region.value
		)
			return;

		const startDate = formatDateForBE(this.timePeriodForm.controls.timePeriod.value.start);
		const endDate = formatDateForBE(this.timePeriodForm.controls.timePeriod.value.end);

		if (!startDate || !endDate) return;

		this._averageCarbonIntensityFilters$.next({ startDate, endDate, tenantId: this._tenantId });
		this._carbonIntensityFilters$.next({
			region: this.regionForm.controls.region.value,
			startDate,
			endDate
		});
		this.onMapRefresh();
	}

	/**
	 * Handles the refresh action for the regions map chart.
	 *
	 * This method sets the loading state for the regions map chart, fetches
	 * all carbon intensity data from the `_whatIfCarbonIntensityService`, and
	 * updates the chart data upon successful retrieval. The loading state is
	 * reset regardless of whether the data retrieval succeeds or fails.
	 *
	 * The observable is automatically unsubscribed when the component is destroyed
	 * to prevent memory leaks.
	 */
	public onMapRefresh() {
		this.isRegionsMapChartLoading = true;

		this._whatIfCarbonIntensityService
			.getAllCarbonIntensityData()
			.pipe(
				tap({
					next: regionsMapChart => this._updateMap(regionsMapChart),
					finalize: () => (this.isRegionsMapChartLoading = false)
				})
			)
			.subscribe();
	}

	/**
	 * Updates the map chart data and applies visual overrides for the regions map.
	 *
	 * @param map - The chart response containing the map chart data and regions information.
	 *              This includes the data necessary to render the map and its associated regions.
	 */
	private _updateMap(map: ChartResponse<MapChart>) {
		this.regionsMapChart = map;
		this.regionsMapChartOverrides = {
			visualMap: { pieces: getRangesColorForMapItemAreaColor() }
		};
	}

	/** Updates the filters for the average carbon intensity chart. */
	public onApplyForRegionalEmissions() {
		if (!this.timePeriodForm?.controls?.timePeriod.value) return;

		const startDate = formatDateForBE(this.timePeriodForm.controls.timePeriod.value.start);
		const endDate = formatDateForBE(this.timePeriodForm.controls.timePeriod.value.end);

		if (!startDate || !endDate) return;

		this._averageCarbonIntensityFilters$.next({ startDate, endDate, tenantId: this._tenantId });
	}

	/** Updates the filters for carbon intensity using the selected date range and region. */
	public onApplyForEmissionsByRegion() {
		if (
			!this.timePeriodForm?.controls?.timePeriod.value ||
			!this.regionForm?.controls?.region.value
		)
			return;

		const startDate = formatDateForBE(this.timePeriodForm.controls.timePeriod.value.start);
		const endDate = formatDateForBE(this.timePeriodForm.controls.timePeriod.value.end);
		const region = this.regionForm.controls.region.value;

		if (!startDate || !endDate) return;

		this._carbonIntensityFilters$.next({ region, startDate, endDate });
	}
}
